import { zodResolver } from '@hookform/resolvers/zod';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Collapse,
    Divider,
    FormHelperText,
    Grid,
    Paper,
    Popover,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
    useCalculateFeesMutation,
    useGetAssetCalendarAvailabilityQuery,
    useGetAvailabilityQuery,
    useGetCompanyGuestsInfoQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { WaitListRequest } from '../../../../app/Slices/SevenDayApiTypes';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import { formatCurrency, formatTime } from '../../../../shared/utils';
import { ExperienceType } from '../../../enums/experience';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormCalendar, { CheckinDetails } from '../../../shared/FormCalendar';
import {
    CleaningFee,
    FlexibleExperienceCharge,
    FormValues,
} from '../../../types/experience';
import CalculateChargesContent from '../../Bookings/Fees/CalculateChargesContent';
import { getAvailableSpaces } from '../../Reservations/ReservationRequest/Shared/reservationUtils';
import { TimeArray } from '../Owner/Property/TimeArray';

interface Props {
    experience: FormValues;
    numberOfMonths?: number;
    isItineraryItemForm?: boolean;
    onSubmitInParent?: (data: any) => void;
    startMonth?: Date;
    defaultMonth?: Date;
    waitListRequest?: WaitListRequest;
    onRegisterSubmitHandler?: (handler: () => void) => void;
    onPrimaryActionDisabledChange?: (disabled: boolean) => void;
    hidePrimaryActionButton?: boolean;
}

const Schema = z.object({
    date: z.object(
        {
            from: z.date(),
            // 'to' is optional because if it were mandatory and not set yet,
            // the onSubmit handler wouldn't execute. This prevents us from
            // manually setting custom error messages when needed.
            // e.g. when min hire is one night but user selects one day and hits reserve. In that case no error would show up
            to: z.date().optional(),
        },
        { message: 'Date selection required' },
    ),
});

const OptionalSchemaProperty = z.object({
    checkin: z.string(),
    checkout: z.string(),
});
const OptionalSchemaCommercial = z.object({
    commercialSpaceType: z.string(),
    checkin: z.string(),
    checkout: z.string(),
});

const CalendarComponent = ({
    experience,
    isItineraryItemForm,
    numberOfMonths,
    onSubmitInParent,
    startMonth,
    defaultMonth,
    waitListRequest,
    onRegisterSubmitHandler,
    onPrimaryActionDisabledChange,
    hidePrimaryActionButton,
}: Props) => {
    const loggedInUser = UserRole();
    const isStack = useMediaQuery('(min-width:900px) and (max-width:2200px)');
    const [charges, setCharges] = useState<any>();
    const [hidePrice, setHidePrice] = useState(true);
    const [billingDetailsAdded, setBillingDetailsAdded] = useState(false);
    const [isWaitingList, setIsWaitingList] = useState(false);
    const [calculateFees, { isLoading: isCalculating }] =
        useCalculateFeesMutation();

    const hasPerHourCharge = experience?.charges.find(
        (charge: any) => charge?.type && charge?.type.includes('Hour'),
    );

    const flexiblePrice = experience?.charges.find(
        (charge: any) => charge?.chargeType === 'flexiblePrice',
    ) as FlexibleExperienceCharge | undefined;

    const { data: onCreditHold } = useGetCompanyGuestsInfoQuery(
        `/guests/${loggedInUser?.company}`,
        {
            skip: !loggedInUser?.role.includes('guest'),
        },
    );

    useEffect(() => {
        setValue('date', undefined);
        onSubmitInParent?.(undefined);
    }, [experience]);

    const [anchorElTotal, setAnchorElTotal] = useState(null);

    const open = Boolean(anchorElTotal);

    const handleClickTotal = (event: any) => {
        setAnchorElTotal(event.currentTarget);
    };

    const handleClosePriceBreakdown = () => {
        setAnchorElTotal(null);
    };

    const totalRef = useRef(null);

    const showPrice =
        !hidePrice &&
        charges?.bookingTotal?.total > 0 &&
        !loggedInUser?.role.includes('owner') &&
        !hasPerHourCharge;

    const schema =
        experience?.type === 'property' && !loggedInUser?.role.includes('owner')
            ? Schema.merge(OptionalSchemaProperty)
            : experience?.type === 'commercial' &&
                !loggedInUser?.role.includes('owner')
              ? Schema.merge(OptionalSchemaCommercial)
              : Schema;

    //Removed shouldUnregister:true as it would cause the value of checkin and checkout to be set to undefined and therefore the price wouldn't show up by selecting dates. This only happens on local though.
    const methods = useForm({
        resolver: zodResolver(schema),
        mode: 'onChange',
    });
    const navigate = useNavigate();

    // disable if owner attempts to book another company's experience
    const disabled =
        loggedInUser?.role.includes('owner') &&
        experience.owner?.id !== loggedInUser?.company;
    const isPrimaryActionDisabled =
        disabled ||
        !billingDetailsAdded ||
        loggedInUser?.viewOnly ||
        onCreditHold?.onCreditHold;

    const { control, setValue, setError, clearErrors, handleSubmit } = methods;

    const date = useWatch({ control, name: 'date' });
    const checkinTime = useWatch({ control, name: 'checkin' });
    const checkoutTime = useWatch({ control, name: 'checkout' });
    const commercialSpaceType = useWatch({
        control,
        name: 'commercialSpaceType',
    });

    const { data: availability, isFetching } =
        useGetAssetCalendarAvailabilityQuery(
            {
                url: `/modules/${experience?.id}/calendar`,
            },
            { skip: !experience?.id },
        );

    const [request, setRequest] = useState({});

    const { data: commercialAvailability } = useGetAvailabilityQuery(
        {
            url: `/modules/commercial/${experience?.id}/get-availability`,
            body: request,
        },
        {
            skip:
                !date ||
                !experience ||
                experience.type !== ExperienceType.Commercial,
        },
    );

    const theme = useTheme();

    useEffect(() => {
        if (flexiblePrice) {
            setValue('checkin', flexiblePrice?.dayCheckin);
            setValue('checkout', flexiblePrice?.dayCheckout);
            return;
        }
        setValue('checkin', experience?.checkinTime);
        setValue('checkout', experience?.checkoutTime);
    }, []);

    const availableSpaces = getAvailableSpaces(
        commercialAvailability,
        experience,
    );

    useEffect(() => {
        if (date?.from && date?.to) {
            setRequest({
                startDate: format(date?.from, 'yyyy-MM-dd'),
                endDate: format(date?.to, 'yyyy-MM-dd'),
                checkinTime,
                checkoutTime,
            });
        }
    }, [date, checkinTime, checkoutTime]);

    useEffect(() => {
        if (date?.from && date?.to) {
            calculateFees({
                bookerType: loggedInUser?.role.includes('owner')
                    ? 'owner'
                    : 'guest',
                module: { id: experience?.id },
                startDate: moment(date.from).format('YYYY-MM-DD'),
                endDate: moment(date.to).format('YYYY-MM-DD'),
                onBehalf: true,
                bookingInfo: {
                    moduleType: experience?.type,
                    visitType: 'business',
                    checkinTime: [
                        ExperienceType.Property,
                        ExperienceType.Commercial,
                    ].includes(experience?.type)
                        ? checkinTime
                        : '08:00',
                    checkoutTime: [
                        ExperienceType.Property,
                        ExperienceType.Commercial,
                    ].includes(experience?.type)
                        ? checkoutTime
                        : '17:00',
                    adults: 1,
                    children: 0,
                    overnightGuests: 1,
                    requiresCatering: false,
                    cateringNotes: '',
                    requiresBeverages: false,
                    beverageNotes: '',
                    requiresMassages: false,
                    massageNotes: '',
                    notes: '',
                    desks: commercialSpaceType === 'desk' ? 1 : 0,
                    privateOffices:
                        commercialSpaceType === 'privateOffice' ? 1 : 0,
                    meetingRooms: commercialSpaceType === 'meetingRoom' ? 1 : 0,
                    entireVenue:
                        commercialSpaceType === 'entireVenue' ? true : false,
                    guests: 1,
                    drivers: [],
                    driverSameAsBooker: true,
                },
                requiresServices: false,
                policies: [],
                staffAttending: [],
                externalAttendees: [],
                setMenus: [],
            }).then((data: any) => {
                if (data) {
                    if (data?.error) {
                        setHidePrice(true);
                    } else {
                        setHidePrice(false);
                        setCharges(data.data);
                    }
                }
            });
        } else {
            setHidePrice(true);
        }
    }, [checkinTime, checkoutTime, date, commercialSpaceType]);

    const [limitedDates, setLimitedDates] = useState<
        Record<string, CheckinDetails>
    >({});

    const [alert, setAlert] = useState<string>('');

    const [checkout, setCheckout] = useState<string>('');
    const [checkin, setCheckin] = useState<string>('');

    const handleSelect = (date?: DateRange) => {
        setAlert('');
        clearErrors('date');

        if (experience?.minHireHours <= 24 && !date?.to) {
            setValue('date', {
                from: date?.from,
                to: date?.from,
            });
        } else {
            setValue('date', date);
        }

        setCheckin('');
        setCheckout('');

        if (!date) return;

        const checkin = date.from && format(date.from, 'yyyy-MM-dd');
        const checkout = date.to && format(date.to, 'yyyy-MM-dd');

        let checkinDetails;
        let checkoutDetails;

        if (checkin) checkinDetails = limitedDates[checkin];
        if (checkout) checkoutDetails = limitedDates[checkout];

        if (checkinDetails?.checkinFrom) {
            setAlert(
                `Check-in available from ${formatTime(
                    checkinDetails.checkinFrom,
                )}`,
            );
            setValue('checkin', checkinDetails.checkinFrom);
            setCheckin(checkinDetails.checkinFrom);
        }

        if (
            (checkoutDetails?.checkoutBefore && !checkoutDetails.checkinFrom) ||
            (checkoutDetails?.checkoutBefore &&
                checkoutDetails.checkinFrom &&
                checkin !== checkout)
        ) {
            setAlert(
                `Checkout before ${formatTime(checkoutDetails.checkoutBefore)}`,
            );
            setValue('checkout', checkoutDetails.checkoutBefore);
            setCheckout(checkoutDetails.checkoutBefore);
        }
    };

    const { data: guestCompanyInfo } = useGetCompanyGuestsInfoQuery(
        `/guests/${loggedInUser?.company}`,
        {
            skip:
                loggedInUser?.role === 'admin' ||
                loggedInUser?.role.includes('owner'),
        },
    );

    useEffect(() => {
        // map so that checkin/checkout data is available in O(n)
        if (availability?.limited) {
            const mappedLimitedDates = availability.limited.reduce(
                (acc: Record<string, CheckinDetails>, day: any) => {
                    acc[day.date] = {
                        checkinFrom: day.checkinFrom,
                        checkoutBefore: day.checkoutBefore,
                    };
                    return acc;
                },
                {},
            );

            setLimitedDates(mappedLimitedDates);
        }

        //block user from booking an experiencce if account information is not setup

        if (
            loggedInUser?.role.includes('guest') &&
            !guestCompanyInfo?.billingDetailsAdded
        ) {
            setBillingDetailsAdded(false);
        } else {
            setBillingDetailsAdded(true);
        }
    }, [availability, date]);

    const endDate = new Date(
        new Date().setFullYear(
            new Date().getFullYear() +
                (loggedInUser?.role.includes('owner') ? 2 : 1),
        ),
    );

    const onSubmit = (data: FieldValues) => {
        if (
            !loggedInUser?.role.includes('owner') &&
            (!data.date.to ||
                !data.date.from ||
                (data.date.to.getTime() - data.date.from.getTime()) /
                    (60 * 60 * 1000) +
                    24 <
                    experience.minHireHours)
        ) {
            setError('date', {
                message: `The minimum hire period is ${
                    experience?.type === 'commercial'
                        ? `${experience?.minHireHours / 24}`
                        : experience?.minHireHours === 24
                          ? `${experience?.minHireHours / 24} day`
                          : experience?.minHireHours === 48
                            ? `${(experience?.minHireHours - 24) / 24} night`
                            : experience?.minHireHours > 24
                              ? `${(experience?.minHireHours - 24) / 24} nights`
                              : `${experience?.minHireHours} ${
                                    experience?.minHireHours === 1
                                        ? 'hour'
                                        : 'hours'
                                }`
                }`,
            });
            return;
        }

        const times = {
            checkin: loggedInUser?.role.includes('owner')
                ? checkin
                : data.checkin,
            checkinFrom: checkin,
            checkout: loggedInUser?.role.includes('owner')
                ? checkout
                : data.checkout,
            checkoutBefore: checkout,
        };

        const commercialWaitListRequestInfo =
            waitListRequest?.commercialWaitListRequestInfo;
        const reqMeetingRoom =
            commercialWaitListRequestInfo &&
            commercialWaitListRequestInfo.meetingRooms > 0 &&
            (commercialWaitListRequestInfo.desks > 0 ||
                commercialWaitListRequestInfo.privateOffices > 0);

        const state = {
            experience,
            dates: [
                {
                    startDate: data.date?.from,
                    endDate: data.date?.to,
                },
            ],
            times,
            commercialSpaceType,
            reqMeetingRoom,
            notes: waitListRequest?.notes,
            waitListId: waitListRequest?.id,
        };

        if (isItineraryItemForm && onSubmitInParent) {
            onSubmitInParent(state);
        } else {
            navigate(
                `/experiences/${
                    isWaitingList ? 'waitListRequest' : 'reservationRequest'
                }/${experience?.type.toLowerCase()}/${experience?.name
                    .replace(/ /g, '')
                    .toLowerCase()}/${experience?.id}`,
                {
                    state,
                },
            );
        }
    };

    // Expose this form's submit callback to a parent container.
    useEffect(() => {
        if (!onRegisterSubmitHandler) return;
        onRegisterSubmitHandler(() => handleSubmit(onSubmit)());
    }, [onRegisterSubmitHandler, handleSubmit, onSubmit]);

    // Keep parent action button state in sync with local validation state.
    useEffect(() => {
        onPrimaryActionDisabledChange?.(isPrimaryActionDisabled);
    }, [onPrimaryActionDisabledChange, isPrimaryActionDisabled]);

    const spaceTypePlaceHolder =
        availableSpaces?.length === 0
            ? `Select dates${experience?.minHireHours === 24 ? ' first' : ''} ${
                  experience?.minHireHours > 24
                      ? '(minimum ' + experience?.minHireHours / 24 + ' days)'
                      : ''
              }`
            : undefined;

    const checkinOptions = TimeArray.filter((time) => {
        if (!date?.from) return false;

        if (date?.from.getTime() === date?.to?.getTime()) {
            return (
                time.value >= checkin &&
                time.value < experience.checkoutTime &&
                time.value >= experience.checkinTime &&
                (!checkoutTime || time.value < checkoutTime)
            );
        }

        return time.value >= checkin && time.value >= experience.checkinTime;
    });

    const checkoutOptions = TimeArray.filter((time) => {
        if (!date?.to) return false;

        if (date?.from.getTime() === date?.to.getTime() && checkinTime) {
            return (
                (!checkout || time.value <= checkout) &&
                time.value <= experience.checkoutTime &&
                time.value > checkinTime
            );
        }

        return (
            (!checkout || time.value <= checkout) &&
            time.value <= experience.checkoutTime
        );
    });

    const unavailableSpaces = availableSpaces
        .filter((space: { isUnavailable: any }) => space.isUnavailable)
        .map((space: { value: any }) => space.value);

    useEffect(() => {
        if (experience?.type === 'commercial') {
            if (
                unavailableSpaces.includes(commercialSpaceType) ||
                (commercialSpaceType === 'entireVenue' &&
                    unavailableSpaces.length > 0)
            ) {
                setIsWaitingList(true);
            } else {
                setIsWaitingList(false);
            }
        }
    }, [unavailableSpaces, commercialSpaceType]);

    const cleaningFee = experience?.charges.find(
        (charge: any) => charge.chargeType === 'cleaningFee',
    );

    const showFromIndicator =
        !!cleaningFee &&
        (cleaningFee as CleaningFee)?.cleaningFeeType !== 'flat';

    const privateOfficeLabel =
        experience?.details?.subType === 'house' ? 'Rooms' : 'Private Offices';

    const formatAvailablePeriod = (period: any) => {
        const formatPeriodDate = (date: string) => {
            const [year, month, day] = date.split('-');
            return `${day}/${month}/${year}`;
        };
        const dateLabel =
            period.from === period.to
                ? formatPeriodDate(period.from)
                : `${formatPeriodDate(period.from)} - ${formatPeriodDate(
                      period.to,
                  )}`;
        return `${dateLabel}: ${formatTime(period.startTime)} - ${formatTime(
            period.endTime,
        )}`;
    };

    const getSpacesWithAvailablePeriods = (spaces: any[]) =>
        (spaces ?? []).filter(
            (space: any) => space?.availablePeriods?.length > 0,
        );

    const renderAvailabilityTooltip = (spaces: any[]) => (
        <Box sx={{ p: 0.5 }}>
            {spaces.map((space: any) => (
                <Box key={space.id} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {space.assignedName}
                    </Typography>
                    {space.availablePeriods.map(
                        (period: any, index: number) => (
                            <Typography key={index} sx={{ fontSize: '11px' }}>
                                {formatAvailablePeriod(period)}
                            </Typography>
                        ),
                    )}
                </Box>
            ))}
        </Box>
    );

    const renderSpaceAvailabilityRow = (
        label: string,
        count: number,
        spaces: any[],
    ) => {
        if (count > 0) {
            return (
                <>
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {label}: <strong>{count}</strong>
                    </span>
                    <br />
                </>
            );
        }

        const spacesWithPeriods = getSpacesWithAvailablePeriods(spaces);
        if (spacesWithPeriods.length === 0) return null;

        return (
            <>
                <Tooltip
                    arrow
                    title={renderAvailabilityTooltip(spacesWithPeriods)}
                >
                    <span
                        style={{
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            cursor: 'pointer',
                        }}
                    >
                        {label}: <strong>{count}</strong>
                        <span
                            style={{
                                textDecoration: 'underline',
                                fontSize: '12px',
                            }}
                        >
                            see availability
                        </span>
                    </span>
                </Tooltip>
                <br />
            </>
        );
    };

    useEffect(() => {
        if (
            waitListRequest &&
            waitListRequest.checkinTime &&
            waitListRequest.checkoutTime
        ) {
            setValue('date', {
                from: new Date(waitListRequest.startDate),
                to: new Date(waitListRequest.endDate),
            });
            setValue('checkin', waitListRequest.checkinTime);
            setValue('checkout', waitListRequest.checkoutTime);
        }
        if (waitListRequest?.commercialWaitListRequestInfo) {
            const { desks, meetingRooms, privateOffices, entireVenue } =
                waitListRequest.commercialWaitListRequestInfo;
            const spaceType =
                desks > 0
                    ? 'desk'
                    : privateOffices > 0
                      ? 'privateOffice'
                      : meetingRooms > 0
                        ? 'meetingRoom'
                        : entireVenue
                          ? 'entireVenue'
                          : '';
            setValue('commercialSpaceType', spaceType);
        }
    }, [waitListRequest]);

    const alerts = () => {
        return (
            <Box>
                {disabled && (
                    <Alert
                        severity='error'
                        sx={{
                            fontSize: '12px',
                            width: '100%',
                        }}
                    >
                        You do not own this experience
                    </Alert>
                )}

                {!billingDetailsAdded && (
                    <Alert
                        severity='error'
                        sx={{
                            fontSize: '12px',
                            width: '100%',
                        }}
                    >
                        {loggedInUser?.role === 'guest_admin'
                            ? 'Set up your billing details to make bookings'
                            : `Contact your company admin - ${guestCompanyInfo?.primaryContact} to set up your billing details to make bookings.`}
                    </Alert>
                )}

                {loggedInUser?.viewOnly && (
                    <Alert
                        severity='error'
                        sx={{
                            fontSize: '12px',
                            width: '100%',
                        }}
                    >
                        Looks like you do not have permission to make a booking.
                        Please contact your admin.
                    </Alert>
                )}
            </Box>
        );
    };

    return (
        availability &&
        (!!waitListRequest ? (
            alerts()
        ) : (
            <Card
                sx={{
                    boxShadow: 3,
                    borderRadius: 3,
                    p: 2,
                    width: '100%',
                    minHeight: '280px',
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FormProvider {...methods}>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            style={{ width: '100%' }}
                        >
                            <Stack gap={2} alignItems='center'>
                                <FormCalendar
                                    name='date'
                                    control={control}
                                    experience={experience}
                                    availability={availability}
                                    onSelect={handleSelect}
                                    startMonth={startMonth ?? new Date()}
                                    defaultMonth={defaultMonth}
                                    endMonth={endDate}
                                    captionLayout='dropdown'
                                    numberOfMonths={numberOfMonths ?? 2}
                                    limitedDates={limitedDates}
                                    setIsWaitingList={setIsWaitingList}
                                    isExperiencePage={true}
                                    isItineraryItemForm={isItineraryItemForm}
                                />
                                <Collapse
                                    in={alert !== ''}
                                    sx={{ width: '100%' }}
                                >
                                    <Alert
                                        sx={{
                                            fontSize: '12px',
                                            width: '100%',
                                        }}
                                        severity='info'
                                    >
                                        {alert}
                                    </Alert>
                                </Collapse>

                                {!loggedInUser?.role.includes('owner') &&
                                    [
                                        ExperienceType.Property,
                                        ExperienceType.Commercial,
                                    ].includes(experience?.type) && (
                                        <Grid container spacing={1}>
                                            <Grid
                                                item
                                                xs={12}
                                                lg={isStack ? 12 : 6}
                                            >
                                                <Box
                                                    sx={{
                                                        '& .MuiInputLabel-root':
                                                            {
                                                                fontSize:
                                                                    '14px',
                                                            },
                                                        '& .MuiInputBase-input':
                                                            {
                                                                fontSize:
                                                                    '14px',
                                                            },

                                                        '& input::placeholder':
                                                            {
                                                                fontSize:
                                                                    '10px',
                                                                opacity: 1,
                                                            },
                                                        '& .MuiInputBase-input::placeholder':
                                                            {
                                                                fontSize:
                                                                    '10px',
                                                                opacity: 1,
                                                            },
                                                        '& .MuiAutocomplete-input::placeholder':
                                                            {
                                                                fontSize:
                                                                    '10px',
                                                                opacity: 1,
                                                            },
                                                    }}
                                                >
                                                    <FormAutocomplete
                                                        name='checkin'
                                                        control={control}
                                                        fullWidth
                                                        options={checkinOptions}
                                                        label={`Check-in ${
                                                            date?.from
                                                                ? new Date(
                                                                      date.from,
                                                                  ).toLocaleDateString(
                                                                      'en-AU',
                                                                  )
                                                                : ''
                                                        }`}
                                                        noOptionsText='Select a date'
                                                        slotProps={{
                                                            popper: {
                                                                sx: {
                                                                    '& .MuiAutocomplete-noOptions':
                                                                        {
                                                                            fontSize:
                                                                                '10px',
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                        },
                                                                    '& .MuiAutocomplete-option':
                                                                        {
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                            fontSize:
                                                                                '14px',
                                                                        },
                                                                    '& .MuiAutocomplete-option[aria-selected="true"]':
                                                                        {
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                            fontSize:
                                                                                '14px',
                                                                        },
                                                                    '& .MuiAutocomplete-option[data-focus="true"]':
                                                                        {
                                                                            backgroundColor:
                                                                                'rgba(20,55,74,0.08)',
                                                                        },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={12}
                                                lg={isStack ? 12 : 6}
                                            >
                                                <Box
                                                    sx={{
                                                        '& .MuiInputLabel-root':
                                                            {
                                                                fontSize:
                                                                    '14px',
                                                            },
                                                        '& .MuiInputBase-input':
                                                            {
                                                                fontSize:
                                                                    '14px',
                                                            },

                                                        '& input::placeholder':
                                                            {
                                                                fontSize:
                                                                    '10px',
                                                                opacity: 1,
                                                            },
                                                        '& .MuiInputBase-input::placeholder':
                                                            {
                                                                fontSize:
                                                                    '10px',
                                                                opacity: 1,
                                                            },
                                                        '& .MuiAutocomplete-input::placeholder':
                                                            {
                                                                fontSize:
                                                                    '10px',
                                                                opacity: 1,
                                                            },
                                                    }}
                                                >
                                                    <FormAutocomplete
                                                        name='checkout'
                                                        fullWidth
                                                        control={control}
                                                        options={
                                                            checkoutOptions
                                                        }
                                                        label={`Checkout ${
                                                            date?.to
                                                                ? new Date(
                                                                      date.to,
                                                                  ).toLocaleDateString(
                                                                      'en-AU',
                                                                  )
                                                                : ''
                                                        }`}
                                                        noOptionsText='Select a date'
                                                        slotProps={{
                                                            popper: {
                                                                sx: {
                                                                    '& .MuiAutocomplete-noOptions':
                                                                        {
                                                                            fontSize:
                                                                                '10px',
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                        },
                                                                    '& .MuiAutocomplete-option':
                                                                        {
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                            fontSize:
                                                                                '14px',
                                                                        },
                                                                    '& .MuiAutocomplete-option[aria-selected="true"]':
                                                                        {
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                            fontSize:
                                                                                '14px',
                                                                        },
                                                                    '& .MuiAutocomplete-option[data-focus="true"]':
                                                                        {
                                                                            backgroundColor:
                                                                                'rgba(20,55,74,0.08)',
                                                                        },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    )}
                                {experience?.type === 'commercial' &&
                                    !loggedInUser?.role.includes('owner') && (
                                        <>
                                            <Grid container spacing={1}>
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <FormAutocomplete
                                                        fullWidth
                                                        name='commercialSpaceType'
                                                        control={control}
                                                        placeholder={
                                                            spaceTypePlaceHolder
                                                        }
                                                        label={'Space Type'}
                                                        disabled={
                                                            availableSpaces?.length ===
                                                            0
                                                        }
                                                        shrinkLabel={true}
                                                        options={[
                                                            ...availableSpaces,
                                                            {
                                                                value: 'entireVenue',
                                                                label: `Entire Venue ${
                                                                    commercialAvailability?.entireVenueAvailable
                                                                        ? ``
                                                                        : `(unavailable)`
                                                                }`,
                                                            },
                                                        ]}
                                                        getOptionDisabled={(
                                                            option: any,
                                                        ) =>
                                                            Boolean(
                                                                option?.disabled,
                                                            )
                                                        }
                                                        slotProps={{
                                                            popper: {
                                                                sx: {
                                                                    '& .MuiAutocomplete-noOptions':
                                                                        {
                                                                            fontSize:
                                                                                '10px',
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                        },
                                                                    '& .MuiAutocomplete-option':
                                                                        {
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                            fontSize:
                                                                                '14px',
                                                                        },
                                                                    '& .MuiAutocomplete-option[aria-selected="true"]':
                                                                        {
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .primary,
                                                                            fontSize:
                                                                                '14px',
                                                                        },
                                                                    '& .MuiAutocomplete-option[data-focus="true"]':
                                                                        {
                                                                            backgroundColor:
                                                                                'rgba(20,55,74,0.08)',
                                                                        },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            {date?.from && date?.to && (
                                                <Stack width='100%'>
                                                    <Divider sx={{ mb: 1 }} />
                                                    <Typography
                                                        sx={{
                                                            color: 'red',
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        {`Availability for ${date?.from?.toLocaleDateString(
                                                            'en-AU',
                                                        )}${
                                                            date?.to?.toLocaleDateString(
                                                                'en-AU',
                                                            ) ===
                                                                date?.from?.toLocaleDateString(
                                                                    'en-AU',
                                                                ) || !date?.to
                                                                ? ''
                                                                : ` - ${date?.to?.toLocaleDateString(
                                                                      'en-AU',
                                                                  )}`
                                                        }:`}
                                                    </Typography>
                                                    {!commercialAvailability && (
                                                        <Skeleton
                                                            variant='text'
                                                            sx={{
                                                                fontSize:
                                                                    '1rem',
                                                            }}
                                                        />
                                                    )}
                                                    {commercialAvailability && (
                                                        <Typography variant='caption'>
                                                            {commercialAvailability?.desks >
                                                                0 && (
                                                                <>
                                                                    {' '}
                                                                    <span
                                                                        style={{
                                                                            whiteSpace:
                                                                                'nowrap',
                                                                        }}
                                                                    >
                                                                        Dedicated
                                                                        Desks:{' '}
                                                                        <strong>
                                                                            {
                                                                                commercialAvailability?.desks
                                                                            }
                                                                        </strong>
                                                                    </span>
                                                                    <br />
                                                                </>
                                                            )}
                                                            {renderSpaceAvailabilityRow(
                                                                privateOfficeLabel,
                                                                commercialAvailability?.privateOffices,
                                                                commercialAvailability?.privateOfficeSpaces,
                                                            )}

                                                            {renderSpaceAvailabilityRow(
                                                                'Meeting Rooms',
                                                                commercialAvailability?.meetingRooms,
                                                                commercialAvailability?.meetingRoomSpaces,
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            )}
                                        </>
                                    )}
                                {alerts()}
                                <Collapse sx={{ width: '100%' }} in={showPrice}>
                                    <Grid
                                        onClick={handleClickTotal}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                        }}
                                        ref={totalRef}
                                    >
                                        <Grid
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: 1,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Typography
                                                variant={
                                                    isStack ? 'body3' : 'body1'
                                                }
                                                sx={{
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                Total{' '}
                                            </Typography>
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: theme.palette.text
                                                        .primary,
                                                    fontSize: '14px',
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-end',
                                            }}
                                        >
                                            <Typography
                                                variant={
                                                    isStack ? 'body3' : 'body1'
                                                }
                                                sx={{
                                                    fontWeight: 'bold',
                                                    textDecoration:
                                                        experience?.billingExcluded
                                                            ? 'line-through'
                                                            : 'none',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {showFromIndicator && (
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        From
                                                    </span>
                                                )}{' '}
                                                {formatCurrency(
                                                    charges?.bookingTotal
                                                        ?.total,
                                                )}
                                            </Typography>
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    fontSize: '10px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                }}
                                            >
                                                Includes GST
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Popover
                                        open={open}
                                        onClose={handleClosePriceBreakdown}
                                        anchorEl={anchorElTotal}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                    >
                                        <Paper
                                            sx={{
                                                p: 2,
                                                minWidth: {
                                                    xl: '390px',
                                                },
                                            }}
                                        >
                                            {' '}
                                            <CalculateChargesContent
                                                fees={charges}
                                                moduleId={experience?.id}
                                                moduleType={experience?.type}
                                                spaces={[]}
                                                isExperiencePage={true}
                                                cleaningFee={cleaningFee}
                                            />
                                        </Paper>
                                    </Popover>
                                </Collapse>
                                <Stack width='100%'>
                                    {!hidePrimaryActionButton ? (
                                        <CustomButton
                                            variant='contained'
                                            color={
                                                isWaitingList
                                                    ? 'primary'
                                                    : 'secondary'
                                            }
                                            type={
                                                isItineraryItemForm
                                                    ? 'button'
                                                    : 'submit'
                                            }
                                            onClick={
                                                isItineraryItemForm
                                                    ? () =>
                                                          handleSubmit(
                                                              onSubmit,
                                                          )()
                                                    : undefined
                                            }
                                            disabled={isPrimaryActionDisabled}
                                        >
                                            {isItineraryItemForm
                                                ? 'Select Dates'
                                                : isWaitingList
                                                  ? 'Join Waitlist'
                                                  : 'Reserve Now'}
                                        </CustomButton>
                                    ) : null}
                                    {showPrice &&
                                        !experience?.billingExcluded &&
                                        !isItineraryItemForm && (
                                            <FormHelperText
                                                sx={{
                                                    fontSize: '12px',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                You won't be charged yet
                                            </FormHelperText>
                                        )}

                                    {experience?.billingExcluded && (
                                        <FormHelperText>
                                            Please note that the host has opted
                                            to exclude you from billing for this
                                            particular experience, and as such,
                                            no charges will apply.
                                        </FormHelperText>
                                    )}

                                    {onCreditHold?.onCreditHold && (
                                        <FormHelperText sx={{ color: 'red' }}>
                                            You are on credit hold as your
                                            account is now overdue. Please pay
                                            to continue using 7DAY.
                                        </FormHelperText>
                                    )}
                                </Stack>
                            </Stack>
                        </form>
                    </FormProvider>
                </CardContent>
            </Card>
        ))
    );
};

export default CalendarComponent;
